import type { CSSProperties, ReactNode } from "react";

type Chip = { color: string; label: string };

type Props = {
  chips?: Chip[];
  children: ReactNode;
  background?: string;
  dotColors?: [string, string, string, string];
  align?: "center" | "start";
};

const DEFAULT_BG = "linear-gradient(135deg, #fafaf7 0%, #f4f1ea 100%)";
const DEFAULT_DOTS: [string, string, string, string] = [
  "#D4537E",
  "#7F77DD",
  "#1D9E75",
  "#EF9F27",
];

export function PreviewShell({
  chips = [],
  children,
  background = DEFAULT_BG,
  dotColors = DEFAULT_DOTS,
  align = "center",
}: Props) {
  return (
    <div
      className="relative h-full overflow-hidden rounded-2xl p-8 flex flex-col"
      style={{ background }}
    >
      <span
        className="boo-float-1 absolute h-1.5 w-1.5 rounded-full opacity-60"
        style={{ top: 40, right: 60, background: dotColors[0] }}
      />
      <span
        className="boo-float-2 absolute h-1 w-1 rounded-full opacity-50"
        style={{ top: 120, right: 140, background: dotColors[1] }}
      />
      <span
        className="boo-float-3 absolute h-[5px] w-[5px] rounded-full opacity-50"
        style={{ top: 80, left: 80, background: dotColors[2] }}
      />
      <span
        className="boo-float-1 absolute h-1 w-1 rounded-full opacity-60"
        style={{ bottom: 100, left: 40, background: dotColors[3] }}
      />

      <div
        className={`relative z-10 flex-1 flex min-h-0 ${
          align === "start" ? "items-start" : "items-center"
        }`}
      >
        <div className="w-full">{children}</div>
      </div>

      {chips.length > 0 && (
        <div className="relative z-10 mt-6 flex flex-wrap justify-center gap-2">
          {chips.map((chip, i) => (
            <div
              key={chip.label}
              className="boo-fade-in flex items-center gap-1.5 rounded-full border border-black/[0.08] bg-white px-3 py-1.5 text-xs text-stone-800"
              style={{ animationDelay: `${1500 + i * 200}ms` }}
            >
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: chip.color }} />
              {chip.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const MOCKUP_SHADOW =
  "0 8px 24px rgba(20, 20, 30, 0.06), 0 1px 3px rgba(20, 20, 30, 0.04)";

export function MockupCard({
  children,
  className = "",
  style,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      className={`mx-2 w-full overflow-hidden rounded-xl bg-white ${className}`}
      style={{ boxShadow: MOCKUP_SHADOW, ...style }}
    >
      {children}
    </div>
  );
}

